// FILE: src/pages/hr/AllPermissionsAndCompOffsList.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-hot-toast";
import { ClipboardList, Search, Eye, X, Check, Ban } from "lucide-react"; // ✅ அப்ரூவல் ஐகான்கள் சேர்க்கப்பட்டுள்ளன
import SummaryApi from "../../common/SummaryApi";
import api from "../../common/apiClient";
import SelectDropdown from "../../customStyles/SelectDropdown";
import PageHeader from "../ui/PageHeader";
import ModernTable from "../ui/ModernTable";

const AllPermissionsAndCompOffsList = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("permissions"); // permissions or compoffs

  // Lists Data
  const [permissions, setPermissions] = useState([]);
  const [compOffs, setCompOffs] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Detail Drawer Tracking States (அப்ரூவல் டிராயருக்கான புதிய ஸ்டேட்கள்)
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [approverNote, setApproverNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filtering States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Pagination States
  const [currentPage, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const statusOptions = [
    { label: "All Statuses", id: "all" },
    { label: "Pending", id: "pending" },
    { label: "Approved", id: "approved" },
    { label: "Rejected", id: "rejected" },
    { label: "Cancelled", id: "cancelled" }
  ];

  // 1. FILTER-க்காக ஊழியர்களின் பட்டியலைப் பெறுதல்
  const fetchEmployees = useCallback(async () => {
    try {
      const url = SummaryApi.getAllEmployees?.url || "/api/hr/employees";
      const res = await api.get(url);
      if (res.data.success) {
        setEmployees(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading filter lists:", err);
    }
  }, []);

  // 2. நிறுவனத்தின் அனைத்து Permissions & Comp-offs தரவுகளையும் பெறுதல் (விவ்-ஆல் எண்ட்பாயிண்ட்)
  const fetchGlobalData = useCallback(async () => {
    setLoading(true);
    try {
      const statusFilter = selectedStatus !== "all" ? `&status=${selectedStatus}` : "";
      
      if (activeTab === "permissions") {
        // அட்மின்கான view-all API-க்கு மாற்றப்பட்டுள்ளது
        const url = `${SummaryApi.getAllPermissionList?.url || "/api/hr/permission/view-all"}?all=true&page=${currentPage}&limit=${itemsPerPage}${statusFilter}`;
        const res = await api.get(url);
        if (res.data.success) {
          const list = res.data.data?.requests || res.data.data || [];
          setPermissions(list);
          setTotalItems(res.data.total || res.data.data?.total || list.length);
        }
      } else {
        // அட்மின்கான view-all API-க்கு மாற்றப்பட்டுள்ளது
        const url = `${SummaryApi.getAllCompOffList?.url || "/api/hr/compoff/view-all"}?all=true&page=${currentPage}&limit=${itemsPerPage}${statusFilter}`;
        const res = await api.get(url);
        if (res.data.success) {
          const list = res.data.data?.requests || res.data.data || [];
          setCompOffs(list);
          setTotalItems(res.data.total || res.data.data?.total || list.length);
        }
      }
    } catch (err) {
      toast.error("Error fetching admin logs.");
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedStatus, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchGlobalData();
  }, [fetchGlobalData]);

  // Filters மாறும்போது பக்கத்தை 1 ஆக மாற்றுதல்
  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedDept, selectedStatus, activeTab]);

  // Departments-ஐத் தானாகப் பிரித்தெடுத்தல்
  const departmentOptions = useMemo(() => {
    const depts = [{ label: "All Departments", id: "all" }];
    const seen = new Set();
    employees.forEach(emp => {
      const dept = emp.departmentId;
      if (dept && dept._id && !seen.has(dept._id)) {
        seen.add(dept._id);
        depts.push({ label: dept.name, id: dept._id });
      }
    });
    return depts;
  }, [employees]);

  // Local search filter for table
  const filteredData = useMemo(() => {
    const list = activeTab === "permissions" ? permissions : compOffs;
    return list.filter(row => {
      const emp = row.userId || {};
      const fullName = `${emp.firstName || ""} ${emp.lastName || ""}`.toLowerCase();
      
      const matchesSearch = 
        fullName.includes(searchQuery.toLowerCase()) || 
        (emp.userId && emp.userId.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesDept = 
        selectedDept === "all" || 
        (emp.departmentId === selectedDept || emp.departmentId?._id === selectedDept);

      return matchesSearch && matchesDept;
    });
  }, [permissions, compOffs, activeTab, searchQuery, selectedDept]);

  // 3. அப்ரூவல் / ரிஜெக்ஷன் செயல்பாடுகள் (Approve/Reject Submit Handler)
  const handleProcessRequest = async (status) => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      let url = "";
      if (activeTab === "permissions") {
        url = status === "approved"
          ?  SummaryApi.approvePermission.url(selectedRequest._id)
          :  SummaryApi.rejectPermission.url(selectedRequest._id);
      } else {
        url = status === "approved"
          ? SummaryApi.approveCompOff.url(selectedRequest._id) || `/api/hr/compoff/approve/${selectedRequest._id}`
          : SummaryApi.rejectCompOff.url(selectedRequest._id);
      }

      const res = await api.put(url, { approverNote });

      if (res.data.success) {
        toast.success(`Request ${status} successfully!`);
        await fetchGlobalData(); // அட்டவணையைப் புதுப்பிக்கும்
        closeViewDrawer();
      } else {
        toast.error(res.data.message || "Failed to process request.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error processing request.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Drawer Open & Close Handlers ---
  const openViewDrawer = (request) => {
    setSelectedRequest(request);
    setApproverNote(request.approverNote || "");
    setIsViewOpen(true);
  };

  const closeViewDrawer = () => {
    setIsViewOpen(false);
    setSelectedRequest(null);
    setApproverNote("");
  };

  // Permissions Columns
  const permissionColumns = [
    {
      key: "employee",
      label: "Employee",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs uppercase text-slate-700">
            {row.userId?.profilePicture ? (
              <img src={row.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
            ) : (
              `${row.userId?.firstName?.charAt(0) || ""}${row.userId?.lastName?.charAt(0) || ""}`
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-gray-800">{`${row.userId?.firstName || ""} ${row.userId?.lastName || ""}`}</span>
            <span className="text-[10px] text-gray-400 font-bold">{row.userId?.userId || "No ID"}</span>
          </div>
        </div>
      )
    },
    {
      key: "date",
      label: "Requested Date",
      render: (row) => <span className="font-semibold text-slate-700">{new Date(row.date).toLocaleDateString()}</span>
    },
    {
      key: "time",
      label: "Time Interval",
      render: (row) => <span className="text-slate-600 font-bold">{row.startTime} - {row.endTime}</span>
    },
    {
      key: "duration",
      label: "Duration",
      render: (row) => <span className="text-slate-500 font-semibold">{row.durationMinutes} Mins</span>
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={`px-2.5 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${
          row.status === "approved" ? "bg-green-50 text-green-700 border-green-200" :
          row.status === "pending" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
          "bg-red-50 text-red-700 border-red-200"
        }`}>
          {row.status}
        </span>
      )
    },
    {
      key: "approver",
      label: "Processed By",
      render: (row) => <span className="text-xs text-gray-500 font-semibold">{row.approverId ? `${row.approverId.firstName} ${row.approverId.lastName}` : "-"}</span>
    }
  ];

  // Comp-off Columns
  const compOffColumns = [
    {
      key: "employee",
      label: "Employee",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs uppercase text-slate-700">
            {row.userId?.profilePicture ? (
              <img src={row.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
            ) : (
              `${row.userId?.firstName?.charAt(0) || ""}${row.userId?.lastName?.charAt(0) || ""}`
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-gray-800">{`${row.userId?.firstName || ""} ${row.userId?.lastName || ""}`}</span>
            <span className="text-[10px] text-gray-400 font-bold">{row.userId?.userId || "No ID"}</span>
          </div>
        </div>
      )
    },
    {
      key: "workedDate",
      label: "Worked Sunday / Holiday",
      render: (row) => <span className="font-semibold text-slate-700">{new Date(row.workedDate).toLocaleDateString()}</span>
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => <span className="text-slate-500 italic max-w-sm truncate block font-medium">"{row.reason}"</span>
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={`px-2.5 py-0.5 rounded border text-xs font-bold uppercase tracking-wider ${
          row.status === "approved" ? "bg-green-50 text-green-700 border-green-200" :
          row.status === "pending" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
          "bg-red-50 text-red-700 border-red-200"
        }`}>
          {row.status}
        </span>
      )
    },
    {
      key: "approver",
      label: "Processed By",
      render: (row) => <span className="text-xs text-gray-500 font-semibold">{row.approverId ? `${row.approverId.firstName} ${row.approverId.lastName}` : "-"}</span>
    }
  ];

  // அட்டவணைக்கான அப்ரூவல் ஆக்ஷன்கள் (Table Action Config)
  const tableActions = [
    {
      label: "View & Process Request",
      icon: <Eye className="w-4 h-4 text-blue-600" />,
      onClick: (row) => openViewDrawer(row),
    }
  ];

  return (
    <div className="p-4">
      <PageHeader
        title="Admin Logs: Permissions & Comp-offs"
        description="Global logs and auditing sheets of all permissions and compensatory offs across the organization."
        titleIcon={ClipboardList}
      />

      {/* FILTER SEARCH PANEL */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-end">
        <div className="w-full">
          <label className="block text-sm font-semibold mb-1.5 text-gray-700">Search Employee</label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Name, ID or Email..."
              className="w-full h-10 pl-9 pr-3 border border-gray-200 rounded-md text-sm focus:outline-none"
            />
          </div>
        </div>

        <div className="w-full">
          <SelectDropdown
            label="Filter by Department"
            options={departmentOptions}
            value={selectedDept}
            onChange={(val) => setSelectedDept(val)}
            labelKey="label"
            valueKey="id"
          />
        </div>

        <div className="w-full">
          <SelectDropdown
            label="Filter by Status"
            options={statusOptions}
            value={selectedStatus}
            onChange={(val) => setSelectedStatus(val)}
            labelKey="label"
            valueKey="id"
          />
        </div>
      </div>

      {/* TABS CONTAINER */}
      <div className="overflow-hidden flex flex-col">
        <div className="flex border-b bg-slate-50/50">
          <button
            onClick={() => setActiveTab("permissions")}
            className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "permissions" 
                ? "border-red-600 text-red-600 bg-red-50/10" 
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            All Permissions
          </button>
          <button
            onClick={() => setActiveTab("compoffs")}
            className={`flex-1 py-4 text-center text-xs font-bold uppercase tracking-wider border-b-2 transition-all ${
              activeTab === "compoffs" 
                ? "border-red-600 text-red-600 bg-red-50/10" 
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            All Comp-off Grants
          </button>
        </div>

        {/* Global Applications list with loader */}
        <div className="p-4 relative overflow-hidden">
          {loading && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center z-10 transition-all rounded-2xl">
              <span className="text-xs font-bold text-gray-500 animate-pulse tracking-wide">
                Refreshing logs...
              </span>
            </div>
          )}

          <ModernTable
            columns={activeTab === "permissions" ? permissionColumns : compOffColumns}
            data={filteredData}
            actions={tableActions}
            actionType="dropdown"
            selectable={false}
            stickyColumns={1}
            pagination={{
              totalItems: searchQuery || selectedDept !== "all" ? filteredData.length : totalItems,
              itemsPerPage,
              currentPage,
              onPageChange: (page) => setPage(page),
              onItemsPerPageChange: (limit) => {
                setItemsPerPage(limit);
                setPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────── */}
      {/* RIGHT-SIDE DETAILED APPROVAL / REJECTION DRAWER (Admin Process) */}
      {/* ─────────────────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 top-[57px] md:top-[65px] z-[65] overflow-hidden transition-all duration-300 ${
          isViewOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={closeViewDrawer} />
        
        <div className="absolute inset-y-0 right-0 max-w-full flex">
          <div
            className={`w-screen max-w-md bg-white shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${
              isViewOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {/* Drawer Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Review {activeTab === "permissions" ? "Permission" : "Comp-off"} Request
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">Admin portal to audit, approve or reject requests.</p>
              </div>
              <button
                type="button"
                onClick={closeViewDrawer}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 text-xs">
              
              {/* Employee Summary Card */}
              <div className="bg-slate-50 p-4 rounded-xl border border-gray-100 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center font-bold text-sm uppercase border border-gray-200">
                    {selectedRequest?.userId?.profilePicture ? (
                      <img src={selectedRequest.userId.profilePicture} alt="Avatar" className="h-full w-full rounded-full object-cover" />
                    ) : (
                      `${selectedRequest?.userId?.firstName?.charAt(0) || ""}${selectedRequest?.userId?.lastName?.charAt(0) || ""}`
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800">
                      {selectedRequest?.userId ? `${selectedRequest.userId.firstName} ${selectedRequest.userId.lastName}` : ""}
                    </div>
                    <div className="text-[10px] text-gray-400 font-semibold">{selectedRequest?.userId?.userId || "No ID"}</div>
                  </div>
                </div>

                {/* தற்போதைய நிலை (Current Status badge) */}
                <div className="pt-1">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                    selectedRequest?.status === "approved" ? "bg-green-50 text-green-700 border-green-200" :
                    selectedRequest?.status === "pending" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                    "bg-red-50 text-red-700 border-red-200"
                  }`}>
                    Current Status: {selectedRequest?.status}
                  </span>
                </div>
              </div>

              {/* Dynamic Parameters based on Active Tab */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Request Parameters</h4>
                <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm space-y-3">
                  {activeTab === "permissions" ? (
                    <>
                      <div className="flex justify-between items-center pb-2.5 border-b border-slate-50">
                        <span className="text-gray-500 font-medium">Requested Date:</span>
                        <span className="font-bold text-gray-800">
                          {selectedRequest?.date ? new Date(selectedRequest.date).toLocaleDateString() : ""}
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-2.5 border-b border-slate-50">
                        <span className="text-gray-500 font-medium">Time Interval:</span>
                        <span className="font-bold text-gray-800">{selectedRequest?.startTime} - {selectedRequest?.endTime}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 font-medium">Total Duration:</span>
                        <span className="font-bold text-gray-800">{selectedRequest?.durationMinutes} Minutes</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 font-medium">Worked Sunday/Holiday:</span>
                      <span className="font-bold text-gray-800">
                        {selectedRequest?.workedDate ? new Date(selectedRequest.workedDate).toLocaleDateString() : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Submission Reason */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Employee Reason</h4>
                <div className="p-3.5 bg-slate-50/50 rounded-xl border border-gray-100 text-xs text-gray-700 leading-relaxed font-medium">
                  "{selectedRequest?.reason || "No reasoning specified"}"
                </div>
              </div>

              {/* Action Logs (ஏற்கனவே செயலாக்கப்பட்டிருந்தால் லாக் காட்டும்) */}
              {selectedRequest?.status !== "pending" && (
                <div className="space-y-2 border-t border-gray-100 pt-5">
                  <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Process Logs</h4>
                  <div className="p-3.5 bg-slate-50 border border-gray-100 rounded-xl space-y-1.5">
                    <p className="font-semibold text-gray-700">
                      Processed By: <span className="font-bold text-gray-900">{selectedRequest?.approverId ? `${selectedRequest.approverId.firstName} ${selectedRequest.approverId.lastName}` : "System / Unknown"}</span>
                    </p>
                    <p className="text-[10px] text-gray-400">
                      Action Date: {selectedRequest?.approvedAt ? new Date(selectedRequest.approvedAt).toLocaleString() : selectedRequest?.rejectedAt ? new Date(selectedRequest.rejectedAt).toLocaleString() : "N/A"}
                    </p>
                    <p className="mt-1 leading-relaxed text-gray-600 italic">
                      "Remarks: {selectedRequest?.approverNote || "No notes provided"}"
                    </p>
                  </div>
                </div>
              )}

              {/* Approver Remarks Input (நிலுவையில் இருந்தால் மட்டுமே டைப் செய்யலாம்) */}
              {selectedRequest?.status === "pending" && (
                <div className="space-y-2 border-t border-gray-100 pt-5">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase">Approver Remarks / Note</label>
                  <textarea
                    rows={4}
                    value={approverNote}
                    onChange={(e) => setApproverNote(e.target.value)}
                    placeholder="Enter approval or rejection notes..."
                    className="w-full p-3 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-blue-100"
                  />
                </div>
              )}
            </div>

            {/* Drawer Footer Action Buttons */}
            <div className="px-6 py-4 border-t border-gray-100 bg-slate-50 flex items-center justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closeViewDrawer}
                className="px-5 py-2.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
                disabled={submitting}
              >
                Close View
              </button>

              {/* நிலுவையில் இருந்தால் மட்டுமே அப்ரூவ் / ரிஜெக்ட் பட்டன்கள் காட்டும் */}
              {selectedRequest?.status === "pending" && (
                <>
                  <button
                    type="button"
                    onClick={() => handleProcessRequest("rejected")}
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                    <span>Reject</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleProcessRequest("approved")}
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AllPermissionsAndCompOffsList;